/**
 * Date formatting utilities for US/Central timezone
 */

const CENTRAL_TIMEZONE = 'America/Chicago';

/**
 * Formats a date string to locale string in US/Central timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';

  // If timestamp doesn't have timezone info (no Z or offset), treat it as UTC
  let timestamp = dateString;
  if (!timestamp.endsWith('Z') && !timestamp.match(/[+-]\d{2}:\d{2}$/)) {
    timestamp = timestamp + 'Z';
  }

  const date = new Date(timestamp);

  return date.toLocaleString('en-US', {
    timeZone: CENTRAL_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

/**
 * Formats a date string to date only in US/Central timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);

  return date.toLocaleDateString('en-US', {
    timeZone: CENTRAL_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a date string with custom options in US/Central timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateCustom = (dateString, options = {}) => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);

  return date.toLocaleString('en-US', {
    timeZone: CENTRAL_TIMEZONE,
    ...options,
  });
};

/**
 * Gets the current date in YYYY-MM-DD format for US/Central timezone
 * @returns {string} Current date in YYYY-MM-DD format
 */
export const getCurrentDateString = () => {
  const now = new Date();

  // Format to YYYY-MM-DD in Central timezone
  const formatted = now.toLocaleString('en-US', {
    timeZone: CENTRAL_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  // Convert from MM/DD/YYYY to YYYY-MM-DD
  const [month, day, year] = formatted.split('/');
  return `${year}-${month}-${day}`;
};

/**
 * Adds days to a date string in YYYY-MM-DD format
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} New date in YYYY-MM-DD format
 */
export const addDays = (dateString, days) => {
  // Parse the date string as UTC to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  // Add the days
  date.setUTCDate(date.getUTCDate() + days);

  // Format back to YYYY-MM-DD
  const newYear = date.getUTCFullYear();
  const newMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  const newDay = String(date.getUTCDate()).padStart(2, '0');

  return `${newYear}-${newMonth}-${newDay}`;
};
